import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronDown, ChevronUp, ExternalLink, Pencil, PinOff, RefreshCw, Sparkles } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDateTime, toDateInputValue } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { AI_EXTRAS, FEATURE_STATUS, isFeatureNotEnabledError } from '@/lib/integrations';
import { useFeature } from '@/hooks/useIntegrationStatus';
import { notify } from '@/components/ui/notify';
import { RowActions } from '@/components/ui/row-actions';
import AiMarkdown from './AiMarkdown';
import { ScopeTag } from './AiMessage';
import { providerLabel } from './aiModel';
import { IconTile } from './ui';

const COLLAPSED_HEIGHT = 260;

/** The answer is from before today (Dubai time) and nobody tried today yet — the card asks for a fresh one
 * (at most once a day, also when the AI keeps failing). */
export function isStale(pin, now = new Date()) {
  const today = toDateInputValue(now);
  const answered = toDateInputValue(pin?.answered_at);
  if (answered && answered >= today) return false;
  return toDateInputValue(pin?.last_attempt_at) !== today;
}

function PinnedCard({ pin, aiEnabled, extrasOff = false, refreshing, onRefresh, onUnpin, onRename }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(pin.title);
  const bodyRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const node = bodyRef.current;
    if (node) setOverflows(node.scrollHeight > COLLAPSED_HEIGHT + 8);
  }, [pin.answer]);
  useEffect(() => setTitle(pin.title), [pin.title]);

  const saveTitle = async () => {
    const next = title.trim();
    setEditing(false);
    if (!next || next === pin.title) {
      setTitle(pin.title);
      return;
    }
    await onRename(pin, next);
  };

  const updated = pin.answered_at ? formatDateTime(pin.answered_at, { relativeDay: true }) : '';
  const provider = providerLabel(pin.provider);
  const chatLink = pin.session_id ? `/ai?chat=${encodeURIComponent(pin.session_id)}` : '/ai';

  return (
    <article className="flex min-w-0 flex-col rounded-card border border-line bg-surface shadow-card" data-testid={`ai-pin-${pin.id}`} aria-labelledby={`ai-pin-title-${pin.id}`}>
      <header className="flex items-start gap-2.5 border-b border-line-2 px-4 py-3">
        <IconTile icon={Sparkles} tone="lime" size={28} className="mt-0.5" />
        <div className="min-w-0 flex-1">
          {editing ? (
            <input
              autoFocus
              value={title}
              maxLength={80}
              aria-label="Card name"
              onChange={(event) => setTitle(event.target.value)}
              onBlur={saveTitle}
              onKeyDown={(event) => {
                if (event.key === 'Enter') { event.preventDefault(); saveTitle(); }
                if (event.key === 'Escape') { event.preventDefault(); setTitle(pin.title); setEditing(false); }
              }}
              className="h-7 w-full rounded-md border border-[color:var(--brand-ink)] bg-surface px-2 text-[14px] font-bold text-ink outline-none"
            />
          ) : (
            <h3 id={`ai-pin-title-${pin.id}`} className="truncate text-[14px] font-bold text-ink" title={pin.prompt}>{pin.title}</h3>
          )}
          <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] text-ink-3">
            {pin.scope?.project_name ? <ScopeTag name={pin.scope.project_name} /> : null}
            <span>{refreshing ? 'Refreshing…' : updated ? `Updated ${updated}` : 'Not answered yet'}</span>
            {provider && !refreshing ? <span className="rounded-md border border-line bg-surface-2 px-1.5 text-[11px] font-semibold text-ink-2">{provider}</span> : null}
          </p>
        </div>
        <RowActions
          label={`Actions for ${pin.title}`}
          items={[
            { label: 'Refresh now', icon: RefreshCw, onSelect: () => onRefresh(pin, { manual: true }), disabled: !aiEnabled || refreshing, hint: aiEnabled ? undefined : (extrasOff ? 'AI extras switched off' : 'AI not set up') },
            { label: 'Open the chat', icon: ExternalLink, onSelect: () => navigate(chatLink) },
            { label: 'Rename', icon: Pencil, onSelect: () => setEditing(true) },
            { label: 'Unpin', icon: PinOff, danger: true, onSelect: () => onUnpin(pin), testId: `ai-pin-unpin-${pin.id}` },
          ]}
          size="sm"
        />
      </header>
      <div className="relative min-w-0 flex-1 px-4 py-3">
        <div ref={bodyRef} className={cn('min-w-0 overflow-hidden', refreshing && 'opacity-60')} style={expanded ? undefined : { maxHeight: COLLAPSED_HEIGHT }}>
          {pin.answer ? <AiMarkdown text={pin.answer} projectRefs={pin.project_refs} className="text-[13px]" /> : <p className="text-[13px] text-ink-3">No answer yet.</p>}
        </div>
        {overflows && !expanded ? (
          <div className="pointer-events-none absolute inset-x-4 bottom-3 h-12 bg-gradient-to-t from-[color:var(--surface)] to-transparent" aria-hidden="true" />
        ) : null}
      </div>
      <footer className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line-2 px-4 py-2.5 text-[12px] text-ink-3">
        {pin.refresh_status === 'error' && pin.last_error ? (
          <span className="inline-flex items-center gap-1 text-status-amber-ink" data-testid={`ai-pin-error-${pin.id}`}>
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
            {pin.last_error}
          </span>
        ) : !aiEnabled ? (
          <span className="inline-flex items-center gap-1" data-testid={`ai-pin-not-refreshing-${pin.id}`} data-status={extrasOff ? 'off' : 'not_configured'}>
            <AlertTriangle className="h-3.5 w-3.5 text-status-amber-ink" aria-hidden="true" />
            {extrasOff
              ? 'Refreshing is switched off — an admin can switch on AI extras in Admin › Integrations. This card keeps its last answer.'
              : 'The AI is not set up — this card keeps its last answer.'}
          </span>
        ) : (
          <span>Answered again every morning</span>
        )}
        <span className="ml-auto flex items-center gap-3">
          {overflows ? (
            <button type="button" onClick={() => setExpanded((value) => !value)} className="inline-flex items-center gap-1 font-semibold text-brand-ink hover:underline">
              {expanded ? 'Show less' : 'Show all'}
              {expanded ? <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />}
            </button>
          ) : null}
          <Link to={chatLink} className="font-semibold text-brand-ink hover:underline">Ask a follow-up</Link>
        </span>
      </footer>
    </article>
  );
}

/**
 * "Pinned from AI" (25 idea 10): the questions this person pinned on the AI page, each answered again every
 * morning by the job `ai_pinned_questions` — and, when the card is opened later than that and its answer is from
 * an earlier day, once more on the spot (only while AI extras is on — `ai_enabled` of GET /ai/pins; one at a time).
 * Re-asking is an AI extra (`ai_extras`, off until an admin switches it on); the cards keep their last answer
 * meanwhile. Nothing shows without pins.
 */
export default function AiPinnedCards({ className }) {
  const extras = useFeature(AI_EXTRAS);
  const [state, setState] = useState({ status: 'loading', pins: [], aiEnabled: false });
  const [refreshing, setRefreshing] = useState({});
  const autoRefreshedRef = useRef(false);

  const replacePin = (next) => setState((current) => ({
    ...current,
    pins: current.pins.map((pin) => (pin.id === next.id ? { ...pin, ...next } : pin)),
  }));

  const refresh = useCallback(async (pin, { manual = false } = {}) => {
    setRefreshing((current) => ({ ...current, [pin.id]: true }));
    try {
      const response = await api.post(`/ai/pins/${encodeURIComponent(pin.id)}/refresh`);
      replacePin(response.data || {});
      if (manual) {
        if (response.data?.refresh_status === 'error') notify.warning('Could not refresh this card', { description: response.data.last_error });
        else notify.success(response.data?.refreshed ? 'Card refreshed' : 'Already up to date', { description: pin.title });
      }
    } catch (err) {
      if (isFeatureNotEnabledError(err)) setState((current) => ({ ...current, aiEnabled: false }));
      if (manual) notify.error('Could not refresh this card', { description: getErrorMessage(err) });
    } finally {
      setRefreshing((current) => ({ ...current, [pin.id]: false }));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get('/ai/pins')
      .then((response) => {
        if (cancelled) return;
        const data = response.data || {};
        setState({ status: 'ready', pins: Array.isArray(data.items) ? data.items : [], aiEnabled: Boolean(data.ai_enabled) });
      })
      .catch(() => { if (!cancelled) setState({ status: 'error', pins: [], aiEnabled: false }); });
    return () => { cancelled = true; };
  }, []);

  // Self-refreshing: answers from an earlier day are asked again, one card at a time, once per visit.
  useEffect(() => {
    if (state.status !== 'ready' || !state.aiEnabled || autoRefreshedRef.current) return;
    autoRefreshedRef.current = true;
    const stale = state.pins.filter((pin) => isStale(pin));
    (async () => {
      for (const pin of stale) {
        // eslint-disable-next-line no-await-in-loop
        await refresh(pin);
      }
    })();
  }, [state.status, state.aiEnabled, state.pins, refresh]);

  const unpin = async (pin) => {
    try {
      await api.delete(`/ai/pins/${encodeURIComponent(pin.id)}`);
      setState((current) => ({ ...current, pins: current.pins.filter((item) => item.id !== pin.id) }));
      notify.success(`“${pin.title}” unpinned`, {
        undo: async () => {
          const response = await api.post(`/ai/pins/${encodeURIComponent(pin.id)}/restore`);
          setState((current) => ({ ...current, pins: [{ ...pin, ...(response.data || {}) }, ...current.pins] }));
        },
        undoneMessage: 'Pinned again',
      });
    } catch (err) {
      notify.error('Could not unpin', { description: getErrorMessage(err) });
    }
  };

  const rename = async (pin, title) => {
    try {
      const response = await api.put(`/ai/pins/${encodeURIComponent(pin.id)}`, { title });
      replacePin(response.data || { id: pin.id, title });
    } catch (err) {
      notify.error('Could not rename the card', { description: getErrorMessage(err) });
    }
  };

  if (state.status !== 'ready' || !state.pins.length) return null;
  return (
    <section className={cn('min-w-0', className)} aria-labelledby="ai-pins-title" data-testid="dashboard-ai-pins">
      <div className="mb-2.5 flex items-center gap-2">
        <h2 id="ai-pins-title" className="text-[15px] font-bold text-ink">Pinned from AI</h2>
        <span className="rounded-full bg-[color:var(--status-gray-bg)] px-2 py-px text-[12px] font-bold tabular-nums text-ink-2">{state.pins.length}</span>
        <Link to="/ai" className="ml-auto text-[12.5px] font-semibold text-brand-ink hover:underline">Open AI</Link>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {state.pins.map((pin) => (
          <PinnedCard
            key={pin.id}
            pin={pin}
            aiEnabled={state.aiEnabled}
            extrasOff={extras.status === FEATURE_STATUS.OFF}
            refreshing={Boolean(refreshing[pin.id])}
            onRefresh={refresh}
            onUnpin={unpin}
            onRename={rename}
          />
        ))}
      </div>
    </section>
  );
}
